import { useEffect, useRef, useState } from "react";
import { seriesActiveAt, type StudentGroupSummary, type StudentSummary } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { listGroups } from "../groups/groups-api.ts";
import { groupOptionLabel, groupScheduleBlockedText, partialScheduleText } from "./group-text.ts";
import { InviteBlock, type InviteLink } from "./OccurrenceOutcome.tsx";

/**
 * «Кому»: адресат занятия и всё, что из выбора следует.
 *
 * Отдельным файлом, потому что диалог создания занятия и так стоял у потолка в
 * 400 строк, а адресат принёс с собой три новых вопроса: кого показывать
 * неактивным и почему, скольким достанется расписание и кого оно не застало.
 * Диалог оставляет себе то, ради чего его открыли (время, длительность,
 * название, созвон), а «кому» живёт здесь целиком.
 *
 * ⚠️ **Вид занятия человек не выбирает, он выбирает адресата** (решение
 * владельца 30.08.2026, подраздел «Экраны» в `docs/group-lessons-design.md`).
 * Поэтому это ОДИН список с двумя группами вариантов, а не переключатель вида
 * занятия и не два поля.
 *
 * ⚠️ **Своих слов здесь нет.** Всё, что читает человек, лежит в `group-text.ts`
 * чистыми функциями с тестами, и причина записана в шапке того файла:
 * предпроверка на клиенте и отказ сервера обязаны говорить одно и то же.
 *
 * ⚠️ **Читающая ручка групп зовётся прямо из `features/groups`**, а не
 * копируется сюда: импорт из соседней фичи в проекте обычное дело (раздел
 * «Экраны» проекта разбирает это отдельно, `features/archive` ходит в
 * `slides`).
 */

/** Приставка значения списка у ученика. Ключи двух видов живут в одном поле. */
const STUDENT = "s:";
/** Приставка значения списка у группы. */
const GROUP = "g:";

export interface GroupChoice {
  group: StudentGroupSummary;
  /** Почему расписание этой группе не завести, либо `null`. */
  blocked: string | null;
  /**
   * Расписание у группы уже есть: это не отказ, а путь.
   *
   * ⚠️ Такой вариант остаётся выбираемым, в отличие от трёх остальных причин, и
   * выбор ведёт на экран расписания группы. Иначе человеку называют место, где
   * ставится день, и не дают туда двери: неактивный вариант не выбирается
   * вовсе, а одинаковых ссылок под списком было бы столько же, сколько таких
   * групп, и какая из них чья, читатель бы не понял.
   */
  path: boolean;
}

export type Recipient =
  { kind: "student"; userId: string } | { kind: "group"; choice: GroupChoice };

export interface Recipients {
  /** Ученики БЕЗ отвязанных: сервер откажет им `student_not_linked`. */
  students: StudentSummary[] | null;
  groups: GroupChoice[] | null;
  value: string;
  pick: (value: string) => void;
  chosen: Recipient | null;
  /**
   * Имя по идентификатору, ВКЛЮЧАЯ отвязанных.
   *
   * ⚠️ Второй карте здесь взяться неоткуда, и это не запас на будущее: в списке
   * невзятых (`skipped`) приезжают ровно те, кого из выпадающего списка убрали.
   * Поэтому полный список сохраняется картой имён, а показывается отобранный.
   */
  nameOf: (userId: string) => string | undefined;
  /**
   * Ссылки заведённых учеников. Показываются один раз, живут до закрытия окна.
   *
   * ⚠️ **Список, а не одно поле, и это починка потери.** Раньше здесь лежала
   * последняя выданная ссылка, и второе заведение затирало первую молча: завёл
   * Аню, не забрал ссылку, тут же завёл Борю, и ссылки Ани больше нет нигде.
   * Гасить «Завести» до тех пор, пока ссылку не заберут, тоже закрывало бы
   * дыру, но ценой запрета на действие, которое человек имеет полное право
   * сделать. Список не запрещает ничего и не теряет ничего.
   */
  invites: InviteLink[];
  /** Отметить ссылку забранной. Ключ это адрес: своего идентификатора у неё нет. */
  markInviteCopied: (url: string) => void;
  /**
   * Есть ли на экране ссылка, которую ещё не забрали.
   *
   * ⚠️ Функция, а не значение, потому что читают её из обработчика, пережившего
   * круг по сети: пока запрос летит, главная кнопка погашена, а кнопка
   * копирования нет, и признак, снятый до отправки, к ответу уже устаревает.
   */
  unclaimed: () => boolean;
  adding: boolean;
  /** Возвращает, получилось ли: поле ввода очищается только на успехе. */
  addStudent: (name: string) => Promise<boolean>;
}

function choiceOf(group: StudentGroupSummary, hasSchedule: boolean): GroupChoice {
  const archived = group.archivedAt !== null;
  return {
    group,
    blocked: groupScheduleBlockedText({
      archived,
      hasSchedule,
      memberCount: group.memberCount,
      activeMemberCount: group.activeMemberCount,
    }),
    // Архив разбирается раньше расписания и той же причиной: вести на экран
    // пучка убранной с глаз группы значит звать доделывать то, что убрали
    path: hasSchedule && !archived,
  };
}

/**
 * Ученики, группы и выбор одного из них.
 *
 * ⚠️ **Признак «у группы уже есть расписание» считается из списка серий, а не
 * запросом на выбор группы.** В списке групп его нет вовсе, и путей было три.
 * Спросить `getGroupSchedule` в момент выбора значит узнать ответ после того,
 * как человек выбрал: до этой секунды список молчит, и группа с расписанием
 * выглядит как любая другая. Не гасить вариант вовсе значит сказать то же самое
 * ещё позже, отказом на кнопке, когда форма уже заполнена. А
 * `GET /v1/schedule/series` отдаёт преподавателю `groupId` каждой серии, то
 * есть ОДИН запрос параллельно с теми, что диалог делает и так, отвечает сразу
 * про все группы и до того, как список открыли.
 *
 * ⚠️ Своей копии правила при этом не заводится: действующую серию отбирает
 * `seriesActiveAt` из `@edu/shared`, та же функция, которой считает сервер
 * (`activeSeries` в `repo/schedule-groups.ts`). Разойдись отбор, и распущенный
 * пучок навсегда запер бы группе новое расписание.
 *
 * Гонку это не закрывает и закрыть не может: расписание могли завести из другой
 * вкладки, пока человек заполнял форму. На этот случай остаётся отказ
 * `group_schedule_exists`, и слова у него те же (`common.ts`).
 */
export function useRecipients(onError: (e: unknown) => void): Recipients {
  const [students, setStudents] = useState<StudentSummary[] | null>(null);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [groups, setGroups] = useState<GroupChoice[] | null>(null);
  const [value, setValue] = useState("");
  const [invites, setInvites] = useState<InviteLink[]>([]);
  const [adding, setAdding] = useState(false);

  /*
    Тот же список ссылок, читаемый из обработчика: разбор у `unclaimed` в
    описании `Recipients`. Пишется только парой ниже, чтобы два представления
    одного списка не разъехались.
  */
  const fresh = useRef<InviteLink[]>([]);
  const putInvites = (next: InviteLink[]) => {
    fresh.current = next;
    setInvites(next);
  };

  /*
    Отказ показывает диалог одной строкой на всё окно, поэтому обработчик
    приезжает снаружи. Держится он в `ref`, чтобы загрузка не перезапускалась от
    каждой перерисовки родителя: стрелка в аргументе это новое значение на
    каждый вызов.
  */
  const report = useRef(onError);
  useEffect(() => {
    report.current = onError;
  });

  useEffect(() => {
    void (async () => {
      try {
        const [s, g, series] = await Promise.all([
          api.listStudents(),
          listGroups(),
          api.listSeries(),
        ]);
        setNames(new Map(s.students.map((x) => [x.userId, x.displayName])));
        // Отвязанным парам новых занятий не завести: сервер ответит
        // `student_not_linked`, и предлагать это действие значит предлагать отказ
        setStudents(s.students.filter((x) => !x.unlinked));

        const now = Date.now();
        const scheduled = new Set<string>();
        for (const one of series.series) {
          const groupId = one.groupId ?? null;
          if (groupId !== null && seriesActiveAt(one, now)) scheduled.add(groupId);
        }
        setGroups(g.groups.map((one) => choiceOf(one, scheduled.has(one.id))));
      } catch (e) {
        report.current(e);
      }
    })();
  }, []);

  let chosen: Recipient | null = null;
  if (value.startsWith(STUDENT)) {
    chosen = { kind: "student", userId: value.slice(STUDENT.length) };
  } else if (value.startsWith(GROUP)) {
    const id = value.slice(GROUP.length);
    const choice = (groups ?? []).find((one) => one.group.id === id);
    if (choice !== undefined) chosen = { kind: "group", choice };
  }

  const addStudent = async (raw: string): Promise<boolean> => {
    const name = raw.trim();
    if (name === "") return false;
    setAdding(true);
    try {
      const created = await api.createStudent(name);
      setStudents((prev) => [...(prev ?? []), created]);
      setNames((prev) => new Map(prev).set(created.userId, created.displayName));
      setValue(`${STUDENT}${created.userId}`);
      // Дописывается, а не замещает: разбор у `invites` в описании `Recipients`
      putInvites([
        ...fresh.current,
        { name: created.displayName, url: created.inviteUrl, copied: false },
      ]);
      return true;
    } catch (e) {
      report.current(e);
      return false;
    } finally {
      setAdding(false);
    }
  };

  return {
    students,
    groups,
    value,
    pick: setValue,
    chosen,
    nameOf: (userId) => names.get(userId),
    invites,
    markInviteCopied: (url) =>
      putInvites(fresh.current.map((one) => (one.url === url ? { ...one, copied: true } : one))),
    unclaimed: () => fresh.current.some((one) => !one.copied),
    adding,
    addStudent,
  };
}

/**
 * Поле «Кому»: список, заведение нового ученика и цена выбора.
 *
 * ⚠️ Две группы вариантов появляются только тогда, когда группы есть: у
 * преподавателя без единой группы заголовок «Ученики» над всем списком не
 * называет ничего, а обещание в подписи пустого варианта было бы обещанием
 * того, чего на экране нет.
 */
export function OccurrenceRecipient({ to, busy }: { to: Recipients; busy: boolean }) {
  const [newName, setNewName] = useState("");

  const chosen = to.chosen;
  const group = chosen !== null && chosen.kind === "group" ? chosen.choice : null;
  /*
    Скольким достанется расписание, СКАЗАНО ДО НАЖАТИЯ. Иначе цену отказа
    человек узнаёт списком невзятых уже после того, как расписание завелось, то
    есть тогда, когда выбирать поздно. У группы, которая ведёт на свой экран,
    вопрос не задан вовсе: расписание там уже есть.
  */
  const partial =
    group === null || group.path
      ? null
      : partialScheduleText(group.group.activeMemberCount, group.group.memberCount);

  const groups = to.groups ?? [];
  const studentOptions = (to.students ?? []).map((s) => (
    <option key={s.userId} value={`${STUDENT}${s.userId}`}>
      {s.displayName}
    </option>
  ));

  const add = () =>
    void (async () => {
      if (await to.addStudent(newName)) setNewName("");
    })();

  return (
    <>
      <label className="field">
        <span className="lbl">Кому</span>
        <select
          className="fld"
          value={to.value}
          onChange={(e) => to.pick(e.target.value)}
          disabled={to.students === null}
        >
          <option value="">
            {to.students === null
              ? "загрузка…"
              : groups.length === 0
                ? "выберите ученика"
                : "выберите ученика или группу"}
          </option>
          {groups.length === 0 ? (
            studentOptions
          ) : (
            <>
              <optgroup label="Ученики">{studentOptions}</optgroup>
              <optgroup label="Группы">
                {groups.map((one) => (
                  <option
                    key={one.group.id}
                    value={`${GROUP}${one.group.id}`}
                    /*
                      Неактивен тот, кому расписание не завести и вести некуда:
                      причина стоит тут же в скобках, и собирает её
                      `groupOptionLabel`. Группа с уже заведённым расписанием
                      выбирается, потому что она путь, а не отказ
                    */
                    disabled={one.blocked !== null && !one.path}
                  >
                    {groupOptionLabel(one.group.title, one.blocked)}
                  </option>
                ))}
              </optgroup>
            </>
          )}
        </select>
      </label>

      {partial !== null && <p className="hint">{partial}</p>}

      <div className="form form--row">
        <input
          className="fld"
          value={newName}
          placeholder="или заведите нового: имя"
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="button"
          className="btn"
          disabled={busy || to.adding || newName.trim() === ""}
          onClick={add}
        >
          Завести
        </button>
      </div>

      {to.invites.map((one) => (
        <InviteBlock key={one.url} invite={one} onCopied={to.markInviteCopied} />
      ))}
    </>
  );
}
